import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import isEqual from 'lodash/isEqual';
import Button from '../../Button';
import Checkbox from '../../Checkbox';
import { Label } from '../../Form';
import Loader from '../../Loader';
import TranslationProvider from '../../../providers/TranslationProvider';
import PrimaryLocationsDropdown from '../PrimaryLocationsDropdown';
import LocationCheckboxes from '../LocationCheckboxes';
import deepClone from '../../../utils/deepClone';
import './AccountCommunication.scss';
import { asyncLoadingStatusTypes, isAsyncUnresovled } from '../../../constants/asyncLoadingStatuses';
import {
  getUser, getUserProfileLocations, postUserProfileEmailOptout, postUserProfileLocations,
} from '../../../api';

const EMAIL_QUEUE_TYPE_RATE_RESERVATION = 21;

/**
 * statesFromAPIResponse
 * Derives savedState and component state from the `/2/profile/locations` API response
 * @param {Object} profileLocationsRes
 */
export function statesFromAPIResponse(profileLocationsRes = {}, user = {}, href = '') {
  const locations = {};
  if (profileLocationsRes !== undefined && profileLocationsRes?.locations) {
    profileLocationsRes.locations.forEach((locationCode) => {
      locations[locationCode] = true;
    });
  }

  const savedState = {
    primary: profileLocationsRes?.primary || null,
    isMarketable: Boolean(profileLocationsRes?.is_marketable),
    locations,
    isOptedOutOfRatings: (user.email_opt_out || []).includes(EMAIL_QUEUE_TYPE_RATE_RESERVATION),
  };

  const nextState = {
    ...savedState,
    locations: {
      ...savedState.locations,
    },
    isSaving: false,
    error: null,
    arrivedViaOptOutLink: /other_section=expanded/ig.test(href),
  };

  return {
    savedState,
    nextState,
  };
}

const accountCommunicationPropTypes = {
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
  resyLocationsConfigStatus: asyncLoadingStatusTypes.isRequired,
  allLocations: PropTypes.arrayOf(PropTypes.shape({})).isRequired,

};

class AccountCommunication extends Component {
  static propTypes = accountCommunicationPropTypes;

  savedState = null;

  state = {};

  constructor(props) {
    super(props);
    this.ratingsCheckboxRow = React.createRef();
  }

  componentDidMount() {
    // Plain getUser() rather than getUserThunk(): this screen keeps its own copy of the
    // user and only shows fields it saves itself, none of them reservation-derived, so the
    // thunk's store write-back, staleness check and request sharing buy nothing here, while
    // it would add a favorites fetch and analytics re-identification on every load.
    this.loadingPromise = Promise.all([
      getUser(),
      getUserProfileLocations(),
    ])
      .then(([user, profileLocationsResult]) => {
        const {
          savedState,
          nextState,
        } = statesFromAPIResponse(profileLocationsResult, user, window.location.href);
        this.savedState = savedState;
        this.setState(
          nextState
        );
        if (nextState.arrivedViaOptOutLink) {
          const element = this.ratingsCheckboxRow.current;
          try {
            element.scrollIntoView({ behavior: 'smooth' });
          } catch (err) {
            window.scrollTo(0, element.getBoundingClientRect().y);
          }
        }
      })
      .catch(() => {
        this.setState({ error: 'initial-load' });
      });
  }

  setLocationChecked(cityCode, newValue) {
    const { locations } = this.state;
    const newLocations = deepClone(locations);
    if (newValue) {
      newLocations[cityCode] = true;
    } else {
      delete newLocations[cityCode];
    }
    this.setState({ locations: newLocations });
  }

  saveEmailOptOut = () => {
    const { isOptedOutOfRatings } = this.state;
    if (this.savedState.isOptedOutOfRatings !== isOptedOutOfRatings) {
      return postUserProfileEmailOptout({
        id: EMAIL_QUEUE_TYPE_RATE_RESERVATION,
        optIn: !isOptedOutOfRatings,
      });
    }
    return Promise.resolve();
  }

  saveClicked = () => {
    this.analyticsForChanges();
    const { primary, isMarketable, locations } = deepClone(this.state);
    this.setState({ isSaving: true, saveSucceeded: false });
    const params = {
      primaryLocationCode: primary,
      isMarketable,
      locationCodes: Object.keys(locations),
    };
    this.savingPromise = Promise.all([
      postUserProfileLocations(params),
      this.saveEmailOptOut(),
    ])
      .then(([response]) => {
        const { savedState, nextState } = statesFromAPIResponse(response);
        this.savedState = savedState;
        this.setState({ ...nextState, saveSucceeded: true });
        window.scrollTo(0, 0);
      })
      .catch(() => {
        this.setState({ isSaving: false, error: 'save' });
      });
  }

  handleLocationChange = (isChecked, code) => {
    const { locations } = this.state;
    // Note: onChange automatically gets called once for each checkbox
    if (isChecked !== !!(locations[code])) {
      this.setLocationChecked(code, isChecked);
    }
  }

  analyticsForChanges() {
    const { Analytics } = this.props;
    const {
      primary,
      isMarketable,
      isOptedOutOfRatings,
      locations,
    } = this.state;
    const allFieldsChanged = [];

    if (primary !== this.savedState.primary) {
      allFieldsChanged.push('primary_city');
    }

    if (isMarketable !== this.savedState.isMarketable) {
      allFieldsChanged.push('marketing_opt-in');
    }

    if (isOptedOutOfRatings !== this.savedState.isOptedOutOfRatings) {
      allFieldsChanged.push('post-dining');
    }

    const keys = Object.keys(locations).sort();
    const keysSaved = Object.keys(this.savedState.locations).sort();

    if (!isEqual(keys, keysSaved)) {
      allFieldsChanged.push('secondary_city');
    }

    Analytics.event('update communication preferences', {
      field: allFieldsChanged,
      ...(allFieldsChanged.includes('marketing_opt-in') && { 'marketing optin': isMarketable }),
      ...(allFieldsChanged.includes('post-dining') && { 'post-dining optin': !isOptedOutOfRatings }),
    });
  }

  hasUnsavedChanges() {
    const {
      primary,
      isMarketable,
      isOptedOutOfRatings,
      locations,
    } = this.state;
    const keys = Object.keys(locations).sort();
    const keysSaved = Object.keys(this.savedState.locations).sort();
    return (
      (primary !== this.savedState.primary)
      || (isMarketable !== this.savedState.isMarketable)
      || (isOptedOutOfRatings !== this.savedState.isOptedOutOfRatings)
      || (keys.join('\t') !== keysSaved.join('\t'))
    );
  }

  render() {
    const {
      primary,
      isMarketable,
      isSaving,
      error,
      locations,
      isOptedOutOfRatings,
      saveSucceeded,
    } = this.state;

    const { resyLocationsConfigStatus, allLocations } = this.props;

    const titleHeading = (
      <TranslationProvider translationKey="communication_heading">
        <h2 className="AccountPage__title">Manage Your Communication Preferences</h2>
      </TranslationProvider>
    );

    let saveSuccessMessage = null;

    if (saveSucceeded && !this.hasUnsavedChanges()) {
      saveSuccessMessage = (
        <div className="AccountPage__message AccountPage__message--success" role="alert" aria-live="assertive">
          <TranslationProvider translationKey="settings_saved">
            <span>Thanks, your settings have been saved.</span>
          </TranslationProvider>
        </div>
      );
    }

    if (error) {
      return (
        <div className="AccountCommunication">
          {titleHeading}
          <TranslationProvider translationKey="communication_error_message">
            <p className="AccountPage__subtitle">Error.</p>
          </TranslationProvider>
        </div>
      );
    }
    if (this.savedState === null || isAsyncUnresovled(resyLocationsConfigStatus)) {
      return (
        <div className="AccountCommunication">
          {titleHeading}
          <Loader className="AccountPage__loader" />
        </div>
      );
    }
    return (
      <div className="AccountCommunication">
        {titleHeading}
        <TranslationProvider translationKey="communication_subheading">
          <p className="AccountPage__subtitle">Opt in or out of email communications at any time. Resy only shares your information with restaurants you book, with your permission.</p>
        </TranslationProvider>
        {saveSuccessMessage}
        <div className="AccountCommunication__form">
          <Label forInput="accountCommunicationLocationCode">
            <TranslationProvider translationKey="communication_heading_primary">
              Select Your Primary Dining City
            </TranslationProvider>
            <PrimaryLocationsDropdown
              name="accountCommunicationLocationCode"
              locations={allLocations}
              selected={primary}
              onChange={(change) => {
                this.setState({ primary: change.primary });
              }}
            />
          </Label>
          <TranslationProvider translationKey="communication_subheading_primary">
            <div className="body--sm color--text-secondary">Setting a primary dining city ensures we show you nearby restaurants and tailor suggestions to your city.</div>
          </TranslationProvider>
          <TranslationProvider translationKey="communication_heading_marketing">
            <h3 className="AccountCommunication__h3 transform--uppercase">Sign Up for Resy Marketing Emails</h3>
          </TranslationProvider>
          <div className="AccountCommunication__comm-checkbox-row">
            {/* eslint-disable-next-line jsx-a11y/label-has-for, jsx-a11y/label-has-associated-control */}
            <label htmlFor="accountCommunicationIsMarketable">
              <Checkbox
                checked={isMarketable}
                name="accountCommunicationIsMarketable"
                onChange={({ accountCommunicationIsMarketable }) => {
                  this.setState({
                    isMarketable: accountCommunicationIsMarketable,
                  });
                }}
              />
              <TranslationProvider translationKey="marketing_accept">
                {/* eslint-disable-next-line max-len */}
                <div className="AccountCommunication__comm-checkbox-label">Get updates with curated recommendations, hit lists and more. By unchecking this box you will be opted out of marketing emails, however, you will still receive communications regarding your bookings for Resy restaurants, events, and experiences.</div>
              </TranslationProvider>
            </label>
          </div>

          <TranslationProvider translationKey="communication_heading_cities">
            <h3 className="AccountCommunication__h3 transform--uppercase">Stay in the Restaurant Know</h3>
          </TranslationProvider>
          <LocationCheckboxes
            primary={primary}
            allLocations={allLocations}
            selectedLocations={locations}
            onCheckboxChange={this.handleLocationChange}
            disabled={!isMarketable}
          />

          <TranslationProvider translationKey="communication_other_section_title">
            <h3 className="AccountCommunication__h3 transform--uppercase">Other Communications</h3>
          </TranslationProvider>

          <div
            className="AccountCommunication__comm-checkbox-row"
            ref={this.ratingsCheckboxRow}
          >
            {/* eslint-disable-next-line jsx-a11y/label-has-for, jsx-a11y/label-has-associated-control */}
            <label htmlFor="accountCommunicationRatingsEnabled">
              <Checkbox
                checked={!isOptedOutOfRatings}
                name="accountCommunicationRatingsEnabled"
                onChange={({ accountCommunicationRatingsEnabled }) => {
                  this.setState({
                    isOptedOutOfRatings: !accountCommunicationRatingsEnabled,
                  });
                }}
              />
              <TranslationProvider translationKey="communication_receive_rating_emails">
                <div className="AccountCommunication__comm-checkbox-label">Receive post-dining emails to provide feedback to restaurants.</div>
              </TranslationProvider>
            </label>
          </div>

          <Button
            disabled={isSaving || !this.hasUnsavedChanges()}
            processing={isSaving}
            onClick={this.saveClicked}
          >
            <TranslationProvider translationKey="communication_save">
              <span>Save</span>
            </TranslationProvider>
          </Button>
        </div>
      </div>
    );
  }
}

const mapStateToProps = ({ resyLocations }) => ({
  resyLocationsConfigStatus: resyLocations.configStatus,
  allLocations: resyLocations.nav,
});

export { accountCommunicationPropTypes, AccountCommunication as ComponentUnderTest };
export default connect(mapStateToProps)(AccountCommunication);
